import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Loader2, Search, Building2, User as UserIcon, Send, X } from 'lucide-react';
import { toast } from 'sonner';

const toLocalDateStr = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};
const todayStr = () => toLocalDateStr(new Date());

export const SellDayPassPackageModal = ({ open, onClose, api, onSold, venues = [] }) => {
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState({ users: [], organizations: [] });
  const [selectedClient, setSelectedClient] = useState(null); // { type: 'user'|'organization', id, name, email }
  const [quantity, setQuantity] = useState('100');
  const [validityDays, setValidityDays] = useState('180');
  const [startDate, setStartDate] = useState(todayStr());
  const [venueIds, setVenueIds] = useState([]);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setQuery(''); setResults({ users: [], organizations: [] }); setSelectedClient(null);
      setQuantity('100'); setValidityDays('180'); setStartDate(todayStr()); setNotes('');
      // A single-venue deployment has nothing to choose — default it in so
      // the seller isn't blocked on a one-option checklist.
      setVenueIds(venues.length === 1 ? [venues[0].id] : []);
    }
  }, [open, venues]);

  useEffect(() => {
    if (!open || selectedClient) return;
    const q = query.trim();
    if (q.length < 2) { setResults({ users: [], organizations: [] }); return; }
    const handle = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get('/day-pass-packages/client-search', { params: { q } });
        setResults(res.data || { users: [], organizations: [] });
      } catch (e) {
        // silent — typeahead, not critical
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [query, open, selectedClient, api]);

  const submit = async () => {
    if (!selectedClient) {
      toast.error('Select a client to sell the package to');
      return;
    }
    const qty = parseInt(quantity, 10);
    if (!qty || qty <= 0) {
      toast.error('Quantity must be greater than 0');
      return;
    }
    const days = parseInt(validityDays, 10);
    if (!days || days <= 0) {
      toast.error('Validity (days) must be greater than 0');
      return;
    }
    if (!startDate) {
      toast.error('Select a start date');
      return;
    }
    if (startDate < todayStr()) {
      toast.error('Start date cannot be in the past');
      return;
    }
    if (venueIds.length === 0) {
      toast.error('Select at least one venue this package is valid for');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        quantity: qty,
        validity_days: days,
        start_date: startDate,
        venue_ids: venueIds,
        notes: notes || null,
      };
      if (selectedClient.type === 'organization') {
        payload.organization_id = selectedClient.id;
      } else {
        payload.user_id = selectedClient.id;
      }
      const res = await api.post('/day-pass-packages', payload);
      toast.success(`Sold ${qty} Bulk Day Passes to ${selectedClient.name}`);
      onSold?.(res.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to sell package');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="day-pass-sell-modal">
        <DialogHeader>
          <DialogTitle>Sell a Bulk Day Pass Package</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <Label>Client *</Label>
            {selectedClient ? (
              <div className="flex items-center justify-between p-2.5 rounded-lg bg-muted/30 border border-border mt-1">
                <div className="flex items-center gap-2 min-w-0">
                  {selectedClient.type === 'organization' ? <Building2 className="w-4 h-4 text-primary shrink-0" /> : <UserIcon className="w-4 h-4 text-muted-foreground shrink-0" />}
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{selectedClient.name}</p>
                    {selectedClient.email && <p className="text-xs text-muted-foreground truncate">{selectedClient.email}</p>}
                  </div>
                </div>
                <Button type="button" size="icon" variant="ghost" className="h-6 w-6 shrink-0" onClick={() => setSelectedClient(null)}>
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            ) : (
              <div className="relative mt-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder="Search client by name, email, or organization…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  data-testid="day-pass-sell-client-search"
                />
                {searching && <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground" />}
                {(results.users?.length > 0 || results.organizations?.length > 0) && (
                  <div className="absolute z-10 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-border bg-popover shadow-md">
                    {results.organizations?.map((o) => (
                      <button
                        key={`org-${o.id}`}
                        type="button"
                        className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50 flex items-center gap-2"
                        onClick={() => { setSelectedClient({ type: 'organization', id: o.id, name: o.name, email: o.email }); setQuery(''); }}
                      >
                        <Building2 className="w-3.5 h-3.5 text-primary" /> {o.name}
                        <Badge variant="outline" className="ml-auto text-xs">Org</Badge>
                      </button>
                    ))}
                    {results.users?.map((u) => (
                      <button
                        key={`user-${u.id}`}
                        type="button"
                        className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50 flex items-center gap-2"
                        onClick={() => { setSelectedClient({ type: 'user', id: u.id, name: u.name, email: u.email }); setQuery(''); }}
                      >
                        <UserIcon className="w-3.5 h-3.5 text-muted-foreground" /> {u.name}
                        <span className="ml-auto text-xs text-muted-foreground truncate">{u.email}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Quantity *</Label>
              <Input type="number" min="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} data-testid="day-pass-sell-quantity" />
            </div>
            <div>
              <Label>Validity (days) *</Label>
              <Input type="number" min="1" step="1" value={validityDays} onChange={(e) => setValidityDays(e.target.value)} placeholder="e.g. 180" data-testid="day-pass-sell-validity" />
            </div>
          </div>

          <div>
            <Label>Start Date *</Label>
            <Input type="date" min={todayStr()} value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="day-pass-sell-start-date" />
            <p className="text-xs text-muted-foreground mt-1">Passes become usable on this date — leave as today, or pick a future date (e.g. next month) if the client wants to start later.</p>
          </div>

          <div>
            <Label>Valid At *</Label>
            <div className="rounded-md border border-border p-3 max-h-40 overflow-y-auto space-y-2 mt-1">
              {venues.length === 0 ? (
                <p className="text-xs text-muted-foreground">No venues available</p>
              ) : venues.map((v) => (
                <label key={v.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={venueIds.includes(v.id)}
                    onCheckedChange={(checked) => {
                      setVenueIds((prev) => checked ? [...prev, v.id] : prev.filter((id) => id !== v.id));
                    }}
                    data-testid={`day-pass-sell-venue-checkbox-${v.id}`}
                  />
                  {v.name}
                </label>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Passes can only be redeemed at the venue(s) selected here.</p>
          </div>

          <div>
            <Label>Notes (optional)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="day-pass-sell-notes" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={submitting || !selectedClient || venueIds.length === 0} data-testid="day-pass-sell-submit">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Sell Package
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SellDayPassPackageModal;
